import { useTranslation } from 'react-i18next';
import {
  ToggleButton,
  ToolbarIcon,
  TextEditorToolbarSection,
} from '../../styles';
import { useGridLikeFlexChildProps } from 'components/StandaloneToolBar/components/GridLikeFlex';
import { useMarkdownEditor } from 'components/WYSIWYG/MarkdownEditor/context';
import { useEffect, useState } from 'react';

export const ListStyleToolbarSection = () => {
  const { t } = useTranslation();
  const { editor, subscribeToSelectionUpdate, subscribeToUpdate } =
    useMarkdownEditor();
  const [ref, props] = useGridLikeFlexChildProps();

  const [
    {
      orderedListActive,
      bulletListActive,
      horizontalRuleActive,
      canToggleOrderedList,
      canToggleBulletList,
      canSetHorizontalRule,
    },
    setState,
  ] = useState(() => ({
    orderedListActive: editor?.isActive('orderedList'),
    bulletListActive: editor?.isActive('bulletList'),
    horizontalRuleActive: editor?.isActive('horizontalRule'),

    canToggleOrderedList: editor?.can().toggleOrderedList(),
    canToggleBulletList: editor?.can().toggleBulletList(),
    canSetHorizontalRule: editor?.can().setHorizontalRule(),
  }));

  useEffect(() => {
    const handler = ({ editor }) => {
      if (!editor) {
        return;
      }
      setState({
        orderedListActive: editor.isActive('orderedList'),
        bulletListActive: editor.isActive('bulletList'),
        horizontalRuleActive: editor.isActive('horizontalRule'),
        canToggleOrderedList: editor.can().toggleOrderedList(),
        canToggleBulletList: editor.can().toggleBulletList(),
        canSetHorizontalRule:
          editor.can().setHorizontalRule() && editor.isActive('paragraph'),
      });
    };

    const selectionUnsubscribe = subscribeToSelectionUpdate(
      'list-style-toolbar-section',
      handler
    );
    const updateUnsubscribe = subscribeToUpdate(
      'list-style-toolbar-section',
      handler
    );

    return () => {
      selectionUnsubscribe();
      updateUnsubscribe();
    };
  }, [subscribeToSelectionUpdate, subscribeToUpdate]);

  if (!editor) {
    return null;
  }

  return (
    <TextEditorToolbarSection ref={ref} {...props}>
      <ToggleButton
        active={orderedListActive}
        title={t('Numbered List')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={!canToggleOrderedList}
      >
        <ToolbarIcon
          icon="list-ol"
          color={
            canToggleOrderedList
              ? 'var(--color-background-darker)'
              : 'var(--color-background-medium)'
          }
        />
      </ToggleButton>
      <ToggleButton
        active={bulletListActive}
        title={t('Bullet List')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={!canToggleBulletList}
      >
        <ToolbarIcon
          icon="list-ul"
          color={
            canToggleBulletList
              ? 'var(--color-background-darker)'
              : 'var(--color-background-medium)'
          }
        />
      </ToggleButton>
      <ToggleButton
        active={horizontalRuleActive}
        title={t('Horizontal Rule')}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={!canSetHorizontalRule}
      >
        <ToolbarIcon
          icon="horizontal-rule"
          color={
            canSetHorizontalRule
              ? 'var(--color-background-darker)'
              : 'var(--color-background-medium)'
          }
        />
      </ToggleButton>
    </TextEditorToolbarSection>
  );
};
